import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, MessageCircle, Mail, Phone, Clock, Send, Upload, X } from "lucide-react";

import { toast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";

interface ContactSupportProps {
  onBack: () => void;
  onNavigateToChat: () => void;
  onNavigateToMyBoyfriends: () => void;
  onNavigateToProfile: () => void;
  onNavigateToSettings: () => void;
  onCreateNew: () => void;
  user?: {
    email?: string;
    name?: string;
  };
}

export const ContactSupport = ({ 
  onBack, 
  onNavigateToChat, 
  onNavigateToMyBoyfriends, 
  onNavigateToProfile, 
  onNavigateToSettings,
  onCreateNew,
  user
}: ContactSupportProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    subject: "",
    category: "",
    priority: "",
    message: "",
    email: user?.email || "",
    name: user?.name || "",
  });

  const [uploadedPhotos, setUploadedPhotos] = useState<File[]>([]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.subject || !formData.category || !formData.message || !formData.email) {
      // Silent validation failure - no notification
      return;
    }

    // Simulate sending support ticket
    console.log("Support ticket submitted:", formData);
    
    // Silent submission - no notification

    // Reset form
    setFormData({
      subject: "",
      category: "",
      priority: "",
      message: "",
      email: user?.email || "",
      name: user?.name || "",
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    setUploadedPhotos(prev => [...prev, ...imageFiles].slice(0, 5)); // Limit to 5 photos
  };

  const removePhoto = (index: number) => {
    setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="bg-gradient-dreamy min-h-screen pb-16">
      {/* Main Content */}
      <div className="p-4 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-primary/20" style={{ paddingTop: "calc(1rem + var(--safe-area-inset-top, 0px))" }}>
            <Button variant="ghost" size="icon" onClick={onNavigateToProfile}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
              {t('contact_support.title')}
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="p-6 border-primary/20">
                <div className="flex items-center gap-2 mb-6">
                  <MessageCircle className="h-5 w-5" />
                  <h3 className="font-semibold">{t('contact_support.send_us_message')}</h3>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">{t('contact_support.name')}</label>
                      <Input
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        placeholder={t('contact_support.name_placeholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">{t('contact_support.email')}</label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        placeholder={t('contact_support.email_placeholder')}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">{t('contact_support.subject')}</label>
                    <Input
                      value={formData.subject}
                      onChange={(e) => handleInputChange("subject", e.target.value)}
                      placeholder={t('contact_support.subject_placeholder')}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">{t('contact_support.category')}</label>
                      <Select value={formData.category} onValueChange={(value) => handleInputChange("category", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('contact_support.category_placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="technical">{t('contact_support.category.technical')}</SelectItem>
                          <SelectItem value="billing">{t('contact_support.category.billing')}</SelectItem>
                          <SelectItem value="account">{t('contact_support.category.account')}</SelectItem>
                          <SelectItem value="feature">{t('contact_support.category.feature')}</SelectItem>
                          <SelectItem value="content">{t('contact_support.category.content')}</SelectItem>
                          <SelectItem value="privacy">{t('contact_support.category.privacy')}</SelectItem>
                          <SelectItem value="other">{t('contact_support.category.other')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">{t('contact_support.priority')}</label>
                      <Select value={formData.priority} onValueChange={(value) => handleInputChange("priority", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('contact_support.priority_placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">{t('contact_support.priority.low')}</SelectItem>
                          <SelectItem value="medium">{t('contact_support.priority.medium')}</SelectItem>
                          <SelectItem value="high">{t('contact_support.priority.high')}</SelectItem>
                          <SelectItem value="urgent">{t('contact_support.priority.urgent')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">{t('contact_support.message')}</label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => handleInputChange("message", e.target.value)}
                      placeholder={t('contact_support.message_placeholder')}
                      rows={6}
                      required
                    />
                  </div>

                  {/* Photo Upload Section */}
                  <div>
                    <label className="block text-sm font-medium mb-2">{t('contact_support.attach_photos')}</label>
                    <div className="space-y-3">
                      {/* Upload Button */}
                      <div className="flex items-center gap-3">
                        <input
                          type="file"
                          id="photo-upload"
                          multiple
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="photo-upload"
                          className="inline-flex items-center px-4 py-2 bg-card border border-primary/20 rounded-lg hover:bg-card/70 transition-colors cursor-pointer"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {t('contact_support.add_photos')}
                        </label>
                        <span className="text-sm text-muted-foreground">
                          {t('contact_support.photos_hint')}
                        </span>
                      </div>

                      {/* Uploaded Photos */}
                      {uploadedPhotos.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {uploadedPhotos.map((file, index) => (
                            <div key={index} className="relative group">
                              <div className="aspect-square bg-card border border-primary/20 rounded-lg overflow-hidden">
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={`Upload ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <button
                                  type="button"
                                  onClick={() => removePhoto(index)}
                                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1 truncate">
                                {file.name}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <Button type="submit" className="w-full" variant="romantic">
                    <Send className="h-4 w-4 mr-2" />
                    {t('contact_support.send_message')}
                  </Button>
                </form>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">

              {/* Other Contact Methods */}
              <Card className="p-6 border-primary/20">
                <h3 className="font-semibold mb-4">{t('contact_support.other_ways')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-primary" />
                    <div>
                      <div className="font-medium">{t('contact_support.email_label')}</div>
                      <div className="text-sm text-muted-foreground">support@aiboyfriend.app</div>
                    </div>
                  </div>
                  
                </div>
              </Card>

              {/* Tips */}
              <Card className="p-6 border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5">
                <h3 className="font-semibold mb-4">{t('contact_support.tips_title')}</h3>
                <ul className="space-y-2 text-sm">
                  <li>{t('contact_support.tip_screenshots')}</li>
                  <li>{t('contact_support.tip_browser')}</li>
                  <li>{t('contact_support.tip_steps')}</li>
                  <li>{t('contact_support.tip_help_center')}</li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
